import { useMemo } from 'react';
import {
  NeedDto,
  NeedsActionDto,
  SuggestedActionDto,
} from '@act/shared/data-transfer-objects';
import {
  Box,
  IconButton,
  Typography,
  Button,
  CircularProgress,
} from '@mui/material';
import { ArrowLeftIcon, PlusIcon, XIcon } from '@phosphor-icons/react';
import { FormBuilder, HtmlDisplayer, ReemaThemeV2 } from '@act/storybook';
import { GoalsSidePanel } from './GoalsSidePanel';
import { generateDatadogTrackingProps } from '@act/toolkit/environment/environment/user-monitoring';
import { useAddActionForm } from './hooks/use-add-action-form';
import syvaNeedsUtils from '../syvaNeeds.util';

interface NeedsDetailAddActionProps {
  open: boolean;
  onClose: () => void;
  need: NeedDto;
  action?: NeedsActionDto;
  suggestedAction?: SuggestedActionDto;
}

export const NeedsDetailAddAction = ({
  open,
  onClose,
  need,
  action,
  suggestedAction,
}: NeedsDetailAddActionProps) => {
  const {
    control,
    handleSubmit,
    formState,
    resourceDetailsFields,
    addResourceDetail,
    removeResourceDetail,
    resourceReferred,
    createActionLoading,
    onSubmit,
    handleCancel,
    isEditing,
    issue,
    category,
    daysRemaining,
    getFormConfig,
  } = useAddActionForm({
    need,
    action,
    suggestedAction,
    onClose,
    trackOpenState: true,
  });

  const formConfig = useMemo(() => getFormConfig(), [getFormConfig]);

  const remainingDaysDisplay =
    syvaNeedsUtils.formatRemainingDays(daysRemaining);

  const content = useMemo(
    () => (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-start',
            gap: '16px',
            mb: 2,
            flexShrink: 0,
          }}
        >
          <IconButton onClick={handleCancel}>
            <ArrowLeftIcon size={32} />
          </IconButton>
          <Typography sx={{ alignSelf: 'center' }} variant="titleLarge">
            {isEditing ? 'Edit Action' : 'Add Action'}
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            borderRadius: '8px',
            padding: '16px',
            backgroundColor: ReemaThemeV2.LIGHT_MODE.palette.navyShades[100],
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: '6px',
              alignItems: 'center',
            }}
          >
            <Typography variant="subheadMedium" fontWeight={'bold'}>
              Need:{' '}
            </Typography>
            <Typography variant="bodyMedium" fontWeight={'regular'}>
              {issue?.label}
            </Typography>
          </Box>
          <Box>
            <Typography>
              <HtmlDisplayer innerHtml={need.reason} />
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: '49px' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <Typography variant="subheadMedium" fontWeight="bold">
                Category
              </Typography>
              <Typography variant="bodyMedium">{category?.label}</Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <Typography variant="subheadMedium" fontWeight="bold">
                Remaining time
              </Typography>
              <Typography variant="bodyMedium">
                {remainingDaysDisplay}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            padding: '16px 0 16px',
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
          }}
        >
          <FormBuilder
            config={formConfig}
            control={control}
            formState={formState}
          />

          {resourceReferred && (
            <Box sx={{ mt: 3 }}>
              <Typography
                variant="subheadMedium"
                fontWeight="bold"
                sx={{ mb: 2 }}
              >
                Resource Details
              </Typography>
              {resourceDetailsFields.map((field, index) => (
                <Box
                  key={field.key}
                  sx={{
                    mb: 3,
                    p: 2,
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 2,
                    }}
                  >
                    <Typography variant="bodyMedium" fontWeight="medium">
                      Resource {index + 1}
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => removeResourceDetail(index)}
                      startIcon={<XIcon size={16} />}
                      disabled={createActionLoading}
                    >
                      Remove
                    </Button>
                  </Box>

                  <FormBuilder
                    config={[
                      {
                        type: 'input',
                        name: `resourceDetails.${index}.resourceName`,
                        label: 'Resource Name',
                        input: 'text',
                        props: {
                          required: true,
                        },
                      },
                      {
                        type: 'group',
                        items: [
                          {
                            type: 'input',
                            name: `resourceDetails.${index}.contactName`,
                            label: 'Contact Name',
                            input: 'text',
                            props: {},
                          },
                          {
                            type: 'input',
                            name: `resourceDetails.${index}.phoneNumber`,
                            label: 'Phone Number',
                            input: 'phone',
                            props: {},
                          },
                        ],
                      },
                      {
                        type: 'input',
                        name: `resourceDetails.${index}.url`,
                        label: 'Website URL',
                        input: 'text',
                        props: {},
                      },
                      {
                        type: 'group',
                        items: [
                          {
                            type: 'input',
                            name: `resourceDetails.${index}.streetAddress`,
                            label: 'Street Address',
                            input: 'text',
                            props: {},
                          },
                          {
                            type: 'input',
                            name: `resourceDetails.${index}.city`,
                            label: 'City',
                            input: 'text',
                            props: {},
                          },
                        ],
                      },
                      {
                        type: 'group',
                        items: [
                          {
                            type: 'input',
                            name: `resourceDetails.${index}.state`,
                            label: 'State',
                            input: 'text',
                            props: {},
                          },
                          {
                            type: 'input',
                            name: `resourceDetails.${index}.zipcode`,
                            label: 'Zip Code',
                            input: 'text',
                            props: {},
                          },
                        ],
                      },
                    ]}
                    control={control}
                    formState={formState}
                    disabled={createActionLoading}
                  />
                </Box>
              ))}

              <Button
                variant="outlined"
                onClick={addResourceDetail}
                startIcon={<PlusIcon size={16} />}
                disabled={createActionLoading}
                sx={{ mt: 2 }}
              >
                Add Resource
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    ),
    [
      handleCancel,
      isEditing,
      issue?.label,
      need.reason,
      category?.label,
      daysRemaining,
      formConfig,
      resourceReferred,
      resourceDetailsFields,
      control,
      formState,
      createActionLoading,
      removeResourceDetail,
      addResourceDetail,
    ]
  );

  const footerContent = (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 2,
        width: '100%',
      }}
    >
      <Button
        variant="outlined"
        onClick={handleCancel}
        disabled={createActionLoading}
      >
        Cancel
      </Button>
      <Button
        variant="contained"
        onClick={handleSubmit(onSubmit)}
        disabled={createActionLoading}
        startIcon={createActionLoading ? <CircularProgress size={20} /> : null}
        {...generateDatadogTrackingProps(
          isEditing
            ? 'needs-update-action-from-call-submit-button'
            : 'needs-add-action-from-call-submit-button'
        )}
      >
        {createActionLoading
          ? isEditing
            ? 'Updating...'
            : 'Creating...'
          : isEditing
            ? 'Update Action'
            : 'Create Action'}
      </Button>
    </Box>
  );

  return (
    <GoalsSidePanel
      open={open}
      animated={false}
      titleContent={<></>}
      content={content}
      footerContent={footerContent}
    />
  );
};
